import { KeycloakTokenParsed } from 'keycloak-js';
import { AccountInfo } from '@azure/msal-browser';
import { authLogger } from './utils/logger';
import { handleAuthErrorSafely, AuthErrorType } from './utils/errorHandling';

/**
 * Supported authentication providers
 */
export type AuthProvider = 'keycloak' | 'entra' | 'magic-link'; // cspell:disable-line

/**
 * User information interface that works for both Keycloak and Entra ID  // cspell:disable-line
 */
export interface UserInfo {
  id?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  roles?: string[];
  // Additional fields that might be present in either provider
  [key: string]: unknown;
}

/**
 * Unified authentication state interface
 * This interface abstracts both Keycloak and MSAL authentication states  // cspell:disable-line
 */
export interface AuthState {
  initialized: boolean;
  authenticated: boolean;
  user: UserInfo | undefined;
  tokenMinValiditySeconds: number;
  tokenExpiresAt?: Date; // Token expiration timestamp for lifecycle management
  actions: {
    initialize: () => Promise<void>;
    login: () => Promise<void>;
    loginWithRedirect?: () => Promise<void>; // Optional for Entra ID only  // cspell:disable-line
    logout: () => Promise<void>;
    updateToken: (forceRefresh?: boolean) => Promise<boolean>;
    getAccessToken: () => Promise<string | undefined>;
  };
}

/**
 * Configuration interface for Keycloak
 */
export interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
}

/**
 * Configuration interface for Microsoft Entra ID (Azure AD)  // cspell:disable-line
 */
// cspell:disable-next-line
export interface EntraConfig {
  clientId: string;
  authority: string;
  redirectUri: string;
  postLogoutRedirectUri?: string;
  scopes?: string[];
}

/**
 * Runtime configuration interface that includes both auth providers
 */
export interface AuthRuntimeConfig {
  AUTH_PROVIDER?: string;
  // Authentication disable flags
  VITE_DISABLE_AUTH?: string;
  VITE_DISABLE_KEYCLOAK_AUTH?: string;
  // Feature flags
  VITE_USE_REDIRECT_AUTH?: string;
  // Keycloak config
  VITE_KEYCLOAK_URL?: string;
  VITE_KEYCLOAK_REALM?: string;
  VITE_KEYCLOAK_CLIENT_ID?: string;
  // Entra ID config  // cspell:disable-line
  VITE_ENTRA_CLIENT_ID?: string; // cspell:disable-line
  VITE_ENTRA_AUTHORITY?: string; // cspell:disable-line
  VITE_ENTRA_REDIRECT_URI?: string; // cspell:disable-line
  VITE_ENTRA_POST_LOGOUT_REDIRECT_URI?: string; // cspell:disable-line
  VITE_ENTRA_SCOPES?: string; // cspell:disable-line
  [key: string]: unknown;
}

/**
 * Helper type to convert Keycloak token to UserInfo
 */
export function keycloakTokenToUserInfo(
  token?: KeycloakTokenParsed,
): UserInfo | undefined {
  if (!token) return undefined;

  return {
    id: token.sub,
    email: token.email,
    name: token.name,
    given_name: token.given_name,
    family_name: token.family_name,
    preferred_username: token.preferred_username,
    roles: token.realm_access?.roles ?? [],
    ...token, // Include any additional fields
  };
}

/**
 * Helper function to parse JWT token and extract claims
 */
function parseJwtToken(token: string): Record<string, unknown> | null {
  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decodedPayload = atob(paddedPayload);

    return JSON.parse(decodedPayload);
  } catch (error) {
    return handleAuthErrorSafely(
      error,
      'parseJwtToken',
      null,
      AuthErrorType.VALIDATION,
    );
  }
}

/**
 * Helper type to convert MSAL account to UserInfo  // cspell:disable-line
 */
// cspell:disable-next-line
export function msalAccountToUserInfo(
  account: AccountInfo | null | undefined, // cspell:disable-line
): UserInfo | undefined {
  if (!account) return undefined;

  const userInfo: UserInfo = {
    id: account.homeAccountId ?? account.localAccountId ?? '',
    email: account.username ?? '',
    name: account.name ?? '',
    given_name:
      typeof account.idTokenClaims?.given_name === 'string'
        ? account.idTokenClaims.given_name
        : undefined,
    family_name:
      typeof account.idTokenClaims?.family_name === 'string'
        ? account.idTokenClaims.family_name
        : undefined,
    preferred_username: account.username ?? '',
    roles: Array.isArray(account.idTokenClaims?.roles)
      ? (account.idTokenClaims.roles as unknown[]).filter(
          (role): role is string => typeof role === 'string',
        )
      : [],
    ...account.idTokenClaims, // Include any additional claims
  };

  // Extract expiration time from ID token if available
  let tokenExp: number | undefined;

  // First try to get exp from idTokenClaims
  if (account.idTokenClaims?.exp) {
    tokenExp = account.idTokenClaims.exp;
  }

  // If not found in claims, try parsing the raw ID token
  if (!tokenExp && account.idToken) {
    const parsedToken = parseJwtToken(account.idToken);
    if (parsedToken?.exp && typeof parsedToken.exp === 'number') {
      tokenExp = parsedToken.exp;
      // Log when extracting from raw token for debugging
      authLogger.dev(
        '🔍 Extracted exp from raw ID token:',
        tokenExp,
        new Date(tokenExp * 1000).toISOString(),
      );
    }
  }

  // Set the expiration time if we found it
  if (tokenExp) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const currentExp = (userInfo as any).exp;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (userInfo as any).exp = tokenExp;

    // Log when expiration value changes for debugging
    if (currentExp !== tokenExp) {
      authLogger.dev(
        '✅ Set user exp to:',
        tokenExp,
        new Date(tokenExp * 1000).toISOString(),
      );
    }
  }

  return userInfo;
}
